import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/utils-format";
import { Copy, ArrowClockwise, UploadSimple, CheckCircle, XCircle, LinkSimple, FileCsv, Info } from "@phosphor-icons/react";

export default function Imports() {
  const [settings, setSettings] = useState(null);
  const [imports, setImports] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [tab, setTab] = useState("webhook");

  const load = async () => {
    try {
      const [s, i] = await Promise.all([api.get("/imports/settings"), api.get("/imports")]);
      setSettings(s.data);
      setImports(i.data);
    } catch { toast.error("Failed to load import settings"); }
  };
  useEffect(() => { load(); }, []);

  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(`${label} copied`); };

  const regenerate = async () => {
    if (!window.confirm("Regenerate webhook secret? The old secret will stop working immediately.")) return;
    try {
      const r = await api.post("/imports/settings/regenerate-secret");
      setSettings((s) => ({ ...s, webhook_secret: r.data.webhook_secret }));
      toast.success("New secret generated");
    } catch { toast.error("Failed"); }
  };

  const uploadCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const r = await api.post("/imports/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastResult(r.data);
      toast.success(`Imported ${r.data.created.length} order(s), ${r.data.errors.length} error(s)`);
      setCsvFile(null);
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Import failed"); }
    finally { setUploading(false); }
  };

  const appsScript = settings ? `// Setup: In your Google Sheet linked to the form, go to
// Extensions > Apps Script, paste this code, then Save (💾).
// Add a trigger: Triggers (clock icon) > + Add Trigger
//   Function: onFormSubmit · Event source: From spreadsheet · Event type: On form submit

const WEBHOOK_URL = "${settings.webhook_url}";
const WEBHOOK_SECRET = "${settings.webhook_secret}";

function onFormSubmit(e) {
  const sheet = e.range.getSheet();
  const headers = sheet.getRange(1, 1, 1, sheet.getLastColumn()).getValues()[0];
  const row = {};
  headers.forEach((h, i) => { row[h] = e.values[i]; });

  const res = UrlFetchApp.fetch(WEBHOOK_URL, {
    method: "post",
    contentType: "application/json",
    headers: { "X-Webhook-Secret": WEBHOOK_SECRET },
    payload: JSON.stringify(row),
    muteHttpExceptions: true,
  });
  Logger.log(res.getResponseCode() + " " + res.getContentText());
}` : "";

  return (
    <div className="p-8" data-testid="imports-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Data Sync</p>
        <h1 className="text-4xl font-black tracking-tighter">Google Forms Import</h1>
        <p className="text-sm text-zinc-500 mt-2">Automatically create orders from Google Form submissions.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100 rounded-md p-1 mb-4">
          <TabsTrigger value="webhook" className="text-xs uppercase tracking-widest" data-testid="tab-webhook"><LinkSimple size={14} className="mr-1" /> Webhook (Auto)</TabsTrigger>
          <TabsTrigger value="csv" className="text-xs uppercase tracking-widest" data-testid="tab-csv"><FileCsv size={14} className="mr-1" /> CSV Upload</TabsTrigger>
          <TabsTrigger value="history" className="text-xs uppercase tracking-widest" data-testid="tab-history">History ({imports.length})</TabsTrigger>
          <TabsTrigger value="mapping" className="text-xs uppercase tracking-widest" data-testid="tab-mapping"><Info size={14} className="mr-1" /> Field Mapping</TabsTrigger>
        </TabsList>

        <TabsContent value="webhook">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Webhook Endpoint">
              <p className="text-sm text-zinc-600 mb-4">Your Google Form's Apps Script will POST each submission to this URL. New orders are created automatically in real-time.</p>
              <Field label="Webhook URL">
                <div className="flex gap-2">
                  <Input readOnly value={settings?.webhook_url || ""} className="border-zinc-300 rounded-md font-mono text-xs" data-testid="webhook-url" />
                  <Button onClick={() => copy(settings.webhook_url, "URL")} variant="outline" className="border-zinc-300" data-testid="copy-webhook-url"><Copy size={14} /></Button>
                </div>
              </Field>
              <Field label="Webhook Secret">
                <div className="flex gap-2">
                  <Input readOnly value={settings?.webhook_secret || ""} className="border-zinc-300 rounded-md font-mono text-xs" data-testid="webhook-secret" />
                  <Button onClick={() => copy(settings.webhook_secret, "Secret")} variant="outline" className="border-zinc-300" data-testid="copy-webhook-secret"><Copy size={14} /></Button>
                  <Button onClick={regenerate} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="regenerate-secret" title="Regenerate"><ArrowClockwise size={14} /></Button>
                </div>
              </Field>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 flex gap-2">
                <Info size={16} className="text-amber-700 flex-shrink-0" weight="bold" />
                <p className="text-xs text-amber-900">Never share the secret publicly. Regenerating invalidates the old secret and requires updating your Apps Script.</p>
              </div>
            </Card>

            <Card title="Google Apps Script Setup">
              <ol className="text-sm text-zinc-700 space-y-2 mb-3 list-decimal list-inside">
                <li>Open your Google Sheet linked to the Form</li>
                <li>Extensions → Apps Script</li>
                <li>Paste the code below and Save</li>
                <li>Triggers (clock icon) → Add Trigger → <code className="text-xs bg-zinc-100 px-1">onFormSubmit</code> → From spreadsheet → On form submit</li>
              </ol>
              <div className="relative">
                <pre className="text-[10px] bg-zinc-900 text-zinc-100 p-3 rounded overflow-x-auto max-h-64 font-mono" data-testid="apps-script-code">{appsScript}</pre>
                <Button onClick={() => copy(appsScript, "Script")} size="sm" className="absolute top-2 right-2 bg-zinc-800 hover:bg-zinc-700 text-white" data-testid="copy-apps-script"><Copy size={12} className="mr-1" /> Copy</Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="csv">
          <Card title="Upload CSV Export">
            <p className="text-sm text-zinc-600 mb-4">
              Download your Google Form responses as CSV (File → Download → CSV) and upload here. Column headers should match the field mapping (see tab).
            </p>
            <form onSubmit={uploadCsv} className="flex gap-3 items-end" data-testid="csv-form">
              <div className="flex-1">
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">CSV File</Label>
                <Input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} className="border-zinc-300 rounded-md mt-1" data-testid="csv-file-input" />
              </div>
              <Button type="submit" disabled={uploading || !csvFile} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="upload-csv-button">
                <UploadSimple size={16} weight="bold" className="mr-1" /> {uploading ? "Uploading…" : "Import"}
              </Button>
            </form>

            {lastResult && (
              <div className="mt-6 space-y-3" data-testid="csv-result">
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-emerald-300 bg-emerald-50 p-4">
                    <div className="flex items-center gap-2 mb-1"><CheckCircle size={16} weight="bold" className="text-emerald-700" /><p className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">Created</p></div>
                    <p className="text-3xl font-black text-emerald-900">{lastResult.created.length}</p>
                  </div>
                  <div className="border border-red-300 bg-red-50 p-4">
                    <div className="flex items-center gap-2 mb-1"><XCircle size={16} weight="bold" className="text-red-700" /><p className="text-[11px] uppercase tracking-widest text-red-800 font-semibold">Errors</p></div>
                    <p className="text-3xl font-black text-red-900">{lastResult.errors.length}</p>
                  </div>
                </div>

                {lastResult.errors.length > 0 && (
                  <div className="border border-red-200 bg-white">
                    <div className="p-3 border-b border-red-200 bg-red-50 text-sm font-semibold text-red-900">Errors ({lastResult.errors.length})</div>
                    <table className="w-full">
                      <thead><tr className="border-b border-zinc-100"><th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Row</th><th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Customer</th><th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Error</th></tr></thead>
                      <tbody>
                        {lastResult.errors.map((e, i) => (
                          <tr key={i} className="border-b border-zinc-100">
                            <td className="px-3 py-2 text-xs font-mono">{e.row_number || "—"}</td>
                            <td className="px-3 py-2 text-xs">{e.customer || "—"}</td>
                            <td className="px-3 py-2 text-xs text-red-700">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <div className="border border-zinc-200 bg-white" data-testid="imports-history">
            <table className="w-full data-table">
              <thead><tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Source</th>
                <th className="text-left px-4 py-2.5">File</th>
                <th className="text-right px-4 py-2.5">Created</th>
                <th className="text-right px-4 py-2.5">Errors</th>
                <th className="text-left px-4 py-2.5">Uploaded By</th>
              </tr></thead>
              <tbody>
                {imports.map((imp) => (
                  <tr key={imp.import_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`import-${imp.import_id}`}>
                    <td className="px-4 py-3 text-xs">{formatDateTime(imp.created_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-zinc-100 border border-zinc-300 rounded">
                        {imp.source === "google_form_webhook" ? "Webhook" : "CSV"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{imp.filename || "—"}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-emerald-700">{imp.created_count}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-red-700">{imp.error_count}</td>
                    <td className="px-4 py-3 text-xs">{imp.uploaded_by || "webhook"}</td>
                  </tr>
                ))}
                {imports.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">No imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mapping">
          <Card title="Column → Field Mapping">
            <p className="text-sm text-zinc-600 mb-4">
              The importer accepts any of these column names (case-insensitive). Column names marked with * are required.
            </p>
            <table className="w-full data-table">
              <thead><tr><th className="text-left px-3 py-2">Order Field</th><th className="text-left px-3 py-2">Accepted Column Headers</th></tr></thead>
              <tbody>
                {settings?.field_mapping && Object.entries(settings.field_mapping).map(([field, aliases]) => (
                  <tr key={field} className="border-b border-zinc-100">
                    <td className="px-3 py-2 text-sm font-mono">
                      {field}{settings.required_fields.includes(field) && <span className="text-red-600 ml-1">*</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-600">{aliases.join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-xs text-blue-900">
              <strong>Duplicate detection:</strong> An order is rejected if the same email+payment_reference combination already exists.
              Successfully imported orders are set to <strong>Waiting for Payment</strong> status.
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <h3 className="text-lg font-bold tracking-tight mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
